import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/app/Shared";
import { GraduationCap, Award, RotateCcw, CheckCircle2, RefreshCw, Download } from "lucide-react";

const ROLE_LABELS = {
  chef: "Chef", bediening: "Bediening", manager: "Manager",
  admin: "Admin", organization_admin: "Admin",
};

export default function PlatformHaccp() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [busy, setBusy] = useState(null); // user_id currently being updated

  const load = () =>
    api.get("/platform/training/haccp")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Laden mislukt"));
  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const orgs = Array.from(new Set(items.map((i) => i.organization_name).filter(Boolean))).sort();
  const filtered = items.filter((it) => {
    if (orgFilter && it.organization_name !== orgFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (it.name || "").toLowerCase().includes(s)
      || (it.email || "").toLowerCase().includes(s)
      || (it.restaurant_name || "").toLowerCase().includes(s)
      || (it.organization_name || "").toLowerCase().includes(s);
  });

  const override = async (user_id, action, level = null) => {
    setBusy(user_id);
    try {
      await api.post("/platform/training/haccp/override", { user_id, action, level });
      await load();
      toast.success("Aangepast");
    } catch { toast.error("Actie mislukt"); }
    finally { setBusy(null); }
  };

  // Generate a certificate PNG for a user by opening the HACCP iframe headless-style:
  // simpler approach — server generates PDF from a locally-drawn PNG placeholder.
  // For MVP we render a browser canvas with the user's name+resto, encode to PNG, POST.
  const downloadCertificate = async (it) => {
    setBusy(it.user_id);
    try {
      // Draw a minimal canvas certificate on-the-fly
      const canvas = document.createElement("canvas");
      canvas.width = 1200; canvas.height = 850;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 1200, 850);
      // Frame
      ctx.strokeStyle = "#1B2D5C"; ctx.lineWidth = 8;
      ctx.strokeRect(30, 30, 1140, 790);
      // Header band
      ctx.fillStyle = "#1B2D5C"; ctx.fillRect(30, 30, 1140, 90);
      ctx.fillStyle = "#ffffff"; ctx.font = "bold 32px sans-serif"; ctx.textAlign = "center";
      ctx.fillText("STOCKLY · HACCP HYGIËNECODE HORECA", 600, 85);
      // Body
      ctx.fillStyle = "#1B2D5C"; ctx.font = "bold 46px serif";
      ctx.fillText("Certificaat van Voltooiing", 600, 210);
      ctx.font = "20px sans-serif"; ctx.fillStyle = "#52525B";
      ctx.fillText("Hierbij wordt verklaard dat", 600, 270);
      ctx.font = "bold 42px serif"; ctx.fillStyle = "#0F172A";
      ctx.fillText(it.name || "Deelnemer", 600, 340);
      ctx.font = "18px sans-serif"; ctx.fillStyle = "#52525B";
      if (it.restaurant_name) ctx.fillText(`werkzaam bij ${it.restaurant_name}`, 600, 380);
      ctx.font = "20px sans-serif";
      ctx.fillText("alle vijf niveaus (1 t/m 5) van de HACCP / Hygiënecode Horeca toetsapp", 600, 450);
      ctx.fillText("met goed gevolg heeft afgerond, telkens met een score van minimaal 70%.", 600, 485);
      // Footer
      const date = new Date().toLocaleDateString("nl-NL");
      ctx.font = "14px monospace"; ctx.fillStyle = "#94A3B8";
      ctx.textAlign = "left"; ctx.fillText(`Datum: ${date}`, 90, 780);
      ctx.textAlign = "right";
      ctx.fillText(`Certificaatnummer: STK-${it.user_id.slice(0, 8).toUpperCase()}`, 1120, 780);

      const dataUrl = canvas.toDataURL("image/png");
      const png_b64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
      const r = await api.post("/platform/training/haccp/certificate",
        { user_id: it.user_id, png_b64, action: "download_pdf" });
      const bin = atob(r.data.pdf_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = r.data.filename || "certificaat.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Certificaat gedownload");
    } catch { toast.error("Downloaden mislukt"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="platform-haccp-page">
      <PageHeader
        overline="Platform · HACCP"
        title="Examens & certificaten"
        description="Live voortgang van alle deelnemers, over alle organisaties. Beheer examens, verleen certificaten en download PDF's."
        actions={
          <button onClick={load} className="inline-flex items-center gap-2 h-9 px-3 border border-zinc-200 rounded-md text-xs font-semibold hover:border-[#1B2D5C]" data-testid="platform-haccp-refresh">
            <RefreshCw size={13} /> Vernieuwen
          </button>
        }
      />
      <div className="card-flat p-4 mb-4 flex flex-wrap items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Zoek op naam, e-mail, restaurant of organisatie…"
               className="flex-1 min-w-[280px] h-9 border border-zinc-200 rounded-md px-3 text-sm" data-testid="platform-haccp-search" />
        <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                className="h-9 border border-zinc-200 rounded-md px-2 text-sm" data-testid="platform-haccp-org-filter">
          <option value="">Alle organisaties</option>
          {orgs.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <div className="flex items-center gap-4 text-xs text-zinc-500 ml-auto">
          <span><b className="text-zinc-900">{data?.total ?? 0}</b> deelnemers</span>
          <span><b className="text-emerald-700">{data?.certified ?? 0}</b> gecertificeerd</span>
          <span><b className="text-zinc-900">{data?.orgs ?? 0}</b> organisaties</span>
        </div>
      </div>

      <div className="card-flat overflow-hidden" data-testid="platform-haccp-table">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-400 uppercase tracking-wide bg-zinc-50">
            <tr>
              <th className="text-left px-4 py-2">Deelnemer</th>
              <th className="text-left px-4 py-2">Organisatie · Restaurant</th>
              <th className="text-center px-4 py-2">Niveaus (1–5)</th>
              <th className="text-center px-4 py-2">Voortgang</th>
              <th className="text-right px-4 py-2">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.map((it) => (
              <tr key={it.user_id} className="hover:bg-zinc-50" data-testid={`platform-haccp-row-${it.user_id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium">{it.name || it.email}</div>
                  <div className="text-[11px] text-zinc-500">{ROLE_LABELS[it.role] || it.role} · {it.email}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-medium text-zinc-800">{it.organization_name || "—"}</div>
                  <div className="text-zinc-500">{it.restaurant_name || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    {[1, 2, 3, 4, 5].map((L) => {
                      const passed = it.levels_passed.includes(L);
                      const score = it.best_scores?.[L] ?? it.best_scores?.[String(L)];
                      return (
                        <button
                          key={L}
                          disabled={busy === it.user_id}
                          onClick={() => override(it.user_id, "unlock_level", L)}
                          title={score != null ? `Klik om niveau ${L} te overriden — huidig: ${score}%` : `Klik om niveau ${L} te unlocken`}
                          className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold border transition ${
                            passed ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600"
                                   : "bg-zinc-50 text-zinc-400 border-zinc-200 hover:bg-zinc-100"
                          }`}
                          data-testid={`platform-haccp-level-${it.user_id}-${L}`}
                        >{L}</button>
                      );
                    })}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  {it.certified ? (
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[11px] font-semibold">
                      <Award size={11} /> Gecertificeerd
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-500">
                      {it.levels_passed.length}/5{it.last_pct != null ? ` · laatste ${it.last_pct}%` : ""}
                    </span>
                  )}
                  {it.total_retakes > 0 && (
                    <div className="text-[10px] text-orange-600 mt-0.5"
                         title="Aantal keer een niveau opnieuw gedaan">
                      {it.total_retakes}× retake
                    </div>
                  )}
                  {it.override_by && (
                    <div className="text-[10px] text-amber-600 mt-0.5">Handmatig aangepast</div>
                  )}
                  {it.auto_email_sent_at && (
                    <div className="text-[10px] text-emerald-600 mt-0.5" title="Certificaat automatisch gemaild">
                      ✉ Auto-gemaild
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      disabled={busy === it.user_id}
                      onClick={() => override(it.user_id, "mark_passed")}
                      className="inline-flex items-center gap-1 h-7 px-2 border border-emerald-200 hover:border-emerald-400 text-emerald-700 rounded text-[11px] font-semibold disabled:opacity-50"
                      data-testid={`platform-haccp-pass-${it.user_id}`}
                      title="Markeer als geslaagd (alle 5 niveaus)"
                    ><CheckCircle2 size={11} /> Geslaagd</button>
                    <button
                      disabled={busy === it.user_id || !it.certified}
                      onClick={() => downloadCertificate(it)}
                      className="inline-flex items-center gap-1 h-7 px-2 border border-[#1B2D5C]/40 hover:border-[#1B2D5C] text-[#1B2D5C] rounded text-[11px] font-semibold disabled:opacity-30"
                      data-testid={`platform-haccp-download-${it.user_id}`}
                      title="Download certificaat als PDF"
                    ><Download size={11} /> PDF</button>
                    <button
                      disabled={busy === it.user_id}
                      onClick={() => override(it.user_id, "reset")}
                      className="inline-flex items-center h-7 w-7 justify-center border border-zinc-200 hover:border-red-300 text-zinc-500 hover:text-red-600 rounded"
                      data-testid={`platform-haccp-reset-${it.user_id}`}
                      title="Reset voortgang"
                    ><RotateCcw size={11} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-sm text-zinc-500">Geen deelnemers gevonden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
